import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { siteInfo } from '@/mocks/manga';

export default function AboutPage() {
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [formMsg, setFormMsg] = useState('');
  const [formError, setFormError] = useState('');

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const formData = new FormData(form);
    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim()) {
      setFormMsg('提交成功！我们会尽快回复您。');
      setContactForm({ name: '', email: '', message: '' });
      setFormError('');
      return;
    }
    formData.delete('phone_alt');

    try {
      const res = await fetch('https://readdy.ai/api/form/d9ontjiftmtnfcfh0alg', {
        method: 'POST',
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (res.ok && parsed?.code === 'OK') {
        setFormMsg('提交成功！我们会尽快回复您。');
        setContactForm({ name: '', email: '', message: '' });
        setFormError('');
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试。';
        setFormError(msg);
        setFormMsg('');
      }
    } catch {
      setFormError('提交失败，请检查网络连接后重试。');
      setFormMsg('');
    }
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: siteInfo.faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于古古漫画网',
    description: siteInfo.description,
    url: 'https://www.gugumanga.com/about',
    about: {
      '@type': 'Organization',
      name: '古古漫画网',
      description: siteInfo.description,
      foundingDate: siteInfo.founded,
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            <Breadcrumb items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]} />

            <section className="mb-14 mt-6">
              <div className="text-center max-w-3xl mx-auto">
                <h1 className="text-2xl md:text-4xl font-bold text-foreground-900 font-heading mb-4">关于古古漫画网</h1>
                <p className="text-sm md:text-base text-foreground-500 leading-relaxed">
                  {siteInfo.description}
                </p>
              </div>
            </section>

            <section className="mb-14">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-center">
                {[
                  { value: siteInfo.founded, label: '成立时间' },
                  { value: siteInfo.users, label: '用户规模' },
                  { value: siteInfo.comics, label: '作品数量' },
                  { value: siteInfo.updates, label: '更新频率' },
                ].map((stat, i) => (
                  <div key={i} className="bg-background-100 rounded-xl p-6 border border-background-200/70">
                    <p className="text-2xl md:text-3xl font-bold text-primary-500 font-heading">{stat.value}</p>
                    <p className="text-sm text-foreground-500 mt-2">{stat.label}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-14">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading text-center mb-8">我们的优势</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {siteInfo.values.map((v, i) => (
                  <div key={i} className="bg-background-100 rounded-xl p-6 border border-background-200/70 hover:border-primary-200 transition-all duration-300">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 mb-4">
                      <i className={`text-lg text-primary-600 ${
                        ['ri-shield-check-line', 'ri-smartphone-line', 'ri-stack-line', 'ri-timer-flash-line', 'ri-money-cny-circle-line', 'ri-group-line'][i]
                      }`}></i>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-900 mb-2">{v.title}</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{v.description}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-14">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading text-center mb-8">常见问题</h2>
              <div className="max-w-3xl mx-auto space-y-3">
                {siteInfo.faqs.map((faq, i) => (
                  <details key={i} className="group bg-background-100 rounded-xl border border-background-200/70 overflow-hidden">
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-primary-50/50 transition-colors">
                      <h4 className="text-sm font-semibold text-foreground-800 pr-4">{faq.question}</h4>
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                        <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                      </div>
                    </summary>
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-500 leading-relaxed">{faq.answer}</p>
                    </div>
                  </details>
                ))}
              </div>
            </section>

            <section className="mb-14">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading text-center mb-8">联系我们</h2>
              <div className="max-w-4xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-4">
                  {[
                    { icon: 'ri-mail-line', label: '电子邮箱', value: siteInfo.contact.email },
                    { icon: 'ri-weibo-line', label: '微博', value: siteInfo.contact.weibo },
                    { icon: 'ri-wechat-line', label: '微信', value: siteInfo.contact.wechat },
                    { icon: 'ri-phone-line', label: '客服电话', value: siteInfo.contact.phone },
                    { icon: 'ri-map-pin-line', label: '公司地址', value: siteInfo.contact.address },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                        <i className={`${item.icon} text-primary-600`}></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-400">{item.label}</p>
                        <p className="text-sm text-foreground-800 font-medium">{item.value}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="bg-background-100 rounded-xl p-6 border border-background-200/70">
                  <h3 className="text-base font-semibold text-foreground-900 mb-4">发送消息</h3>
                  <form onSubmit={handleContactSubmit} className="space-y-4" data-readdy-form>
                    <div>
                      <input
                        type="text"
                        name="name"
                        value={contactForm.name}
                        onChange={(e) => setContactForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="您的姓名"
                        className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                        required
                      />
                    </div>
                    <div>
                      <input
                        type="email"
                        name="email"
                        value={contactForm.email}
                        onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="您的邮箱"
                        className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                        required
                      />
                    </div>
                    <div>
                      <textarea
                        name="message"
                        value={contactForm.message}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val.length <= 500) setContactForm(prev => ({ ...prev, message: val }));
                        }}
                        placeholder="请输入您想说的话（最多500字）"
                        rows={4}
                        className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        required
                      ></textarea>
                      <p className="text-xs text-foreground-400 mt-1 text-right">{contactForm.message.length}/500</p>
                    </div>
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="honeypot-field"
                    />
                    <button
                      type="submit"
                      className="w-full px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-lg transition-all text-sm whitespace-nowrap cursor-pointer"
                    >
                      发送消息
                    </button>
                    {formMsg && (
                      <p className="text-sm text-accent-600 text-center">{formMsg}</p>
                    )}
                    {formError && (
                      <p className="text-sm text-red-500 text-center">{formError}</p>
                    )}
                  </form>
                </div>
              </div>
            </section>
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}